'use client';

import CustomLoading from '@/components/custom-loading';
import CustomToast from '@/components/custom-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { ProcessActionDirectories } from '@/constant/dictionaries';
import { pageDirectories } from '@/constant/route';
import { useCachedLocalEmployeeInfo } from '@/hooks/cached-local/use-cached-local-employee-info';
import { useEmployeeInfoStore } from '@/hooks/use-employee-store';
import { cn, formatDateTime } from '@/lib/utils';
import { Link, usePathname, useRouter } from '@/navigation';
import {
    hideDossierNotifications,
    readDossierNotifications,
} from '@/server/api/v1/dossier-notification/dossier-notification.action';
import {
    DossierNotificationReadsRequest,
    DossierNotificationResponse,
} from '@/server/api/v1/dossier-notification/type';
import { EmployeeSearchAdvanceResponse } from '@/server/api/v1/employee/type';
import { api } from '@/trpc/client';
import {
    ArrowRight,
    Ban,
    BellOff,
    Check,
    CheckCircle2,
    ChevronRight,
    Clock,
    Eye,
    FileText,
    FolderPlus,
    Inbox,
    Link2,
    type LucideIcon,
    PackageCheck,
    PauseCircle,
    PlayCircle,
    Printer,
    RotateCcw,
    Send,
    Undo2,
    UserCheck,
    X,
    XCircle,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    useDossierNotificationStore,
    useHeaderNotificationDropdownStore,
} from '../utils';

type NotificationListProps = {
    isFetching?: boolean;
    isFetchMore?: boolean;
    hvMoreNotice?: boolean;
    onFetchMore: VoidFunction;
    /**Đánh dấu tất cả đã đọc — từ container truyền xuống, chỉ dùng cho nhân viên */
    onMarkAllRead?: () => Promise<void>;
    isMarkingAll?: boolean;
};

function collectionEmployeeId(arrNotice: DossierNotificationResponse[]) {
    const arrEmpId: string[] = [];
    for (const notice of arrNotice) {
        if (
            notice.ActionTriggerBy &&
            notice.ActionTriggerName === 'Employees'
        ) {
            arrEmpId.push(notice.ActionTriggerBy);
        }
        if (notice.Send_Employee_ID) arrEmpId.push(notice.Send_Employee_ID);
        if (notice.EmployeeId) arrEmpId.push(notice.EmployeeId);
    }
    return { empIds: [...new Set(arrEmpId)] };
}

/**
 * Trả về trạng thái đã xem theo đúng đối tượng:
 * - Người nộp (customer): dùng cột `Customer_IsSeen`
 * - Nhân viên: dùng cột `IsSeen`
 */
function getIsSeenByUser(
    notice: DossierNotificationResponse,
    isCustomer: boolean
): boolean {
    return isCustomer
        ? (notice.Customer_IsSeen ?? false)
        : (notice.IsSeen ?? false);
}

type ActionCategory =
    | 'receive'
    | 'receiveComplete'
    | 'forward'
    | 'returnBack'
    | 'revoke'
    | 'approve'
    | 'refuse'
    | 'print'
    | 'returnResult'
    | 'pause'
    | 'cancel'
    | 'assign'
    | 'connect'
    | 'additional'
    | 'continue'
    | 'general'
    | 'unknown';

type ActionStyle = {
    icon: LucideIcon;
    iconClass: string;
    bgClass: string;
    tooltip: string;
};

/* Màu sắc và icon đại diện cho từng nhóm hành động */
const ACTION_STYLES: Record<ActionCategory, ActionStyle> = {
    receive:         { icon: Inbox,         iconClass: 'text-blue-500',    bgClass: 'bg-blue-100',    tooltip: 'Tiếp nhận hồ sơ' },
    receiveComplete: { icon: PackageCheck,  iconClass: 'text-green-600',   bgClass: 'bg-green-100',   tooltip: 'Hoàn thành tiếp nhận' },
    forward:         { icon: ArrowRight,    iconClass: 'text-purple-500',  bgClass: 'bg-purple-100',  tooltip: 'Chuyển xử lý' },
    returnBack:      { icon: Undo2,         iconClass: 'text-amber-600',   bgClass: 'bg-amber-100',   tooltip: 'Trả lại hồ sơ' },
    revoke:          { icon: RotateCcw,     iconClass: 'text-orange-500',  bgClass: 'bg-orange-100',  tooltip: 'Thu hồi hồ sơ' },
    approve:         { icon: CheckCircle2,  iconClass: 'text-emerald-600', bgClass: 'bg-emerald-100', tooltip: 'Phê duyệt' },
    refuse:          { icon: XCircle,       iconClass: 'text-red-500',     bgClass: 'bg-red-100',     tooltip: 'Từ chối' },
    print:           { icon: Printer,       iconClass: 'text-slate-500',   bgClass: 'bg-slate-100',   tooltip: 'In biên nhận' },
    returnResult:    { icon: Send,          iconClass: 'text-teal-600',    bgClass: 'bg-teal-100',    tooltip: 'Trả kết quả' },
    pause:           { icon: PauseCircle,   iconClass: 'text-yellow-600',  bgClass: 'bg-yellow-100',  tooltip: 'Tạm dừng xử lý' },
    cancel:          { icon: Ban,           iconClass: 'text-rose-500',    bgClass: 'bg-rose-100',    tooltip: 'Hủy hồ sơ' },
    assign:          { icon: UserCheck,     iconClass: 'text-indigo-500',  bgClass: 'bg-indigo-100',  tooltip: 'Phân công xử lý' },
    connect:         { icon: Link2,         iconClass: 'text-cyan-600',    bgClass: 'bg-cyan-100',    tooltip: 'Phối hợp xử lý' },
    additional:      { icon: FolderPlus,    iconClass: 'text-violet-500',  bgClass: 'bg-violet-100',  tooltip: 'Bổ sung hồ sơ' },
    continue:        { icon: PlayCircle,    iconClass: 'text-lime-600',    bgClass: 'bg-lime-100',    tooltip: 'Tiếp tục xử lý' },
    general:         { icon: FileText,      iconClass: 'text-blue-400',    bgClass: 'bg-blue-50',     tooltip: 'Thông báo hồ sơ' },
    unknown:         { icon: FileText,      iconClass: 'text-gray-400',    bgClass: 'bg-gray-100',    tooltip: 'Thông báo mới' },
};

/* Ánh xạ ActionTriggerTrace → nhóm hành động */
const ACTION_TRACE_CATEGORY: Partial<Record<string, ActionCategory>> = {
    EMP_CreatesAndWaitingTransfer:  'receive',
    EMP_ReceiveManyOnline:          'receive',
    EMP_ReceiveOnline:              'receive',
    EMP_ReceiveCompleted:           'receiveComplete',
    AdditionalReceiveRequestPause:  'receiveComplete',
    EMP_Forward:                    'forward',
    EMP_PrevForward:                'returnBack',
    EMP_ReturnAssignTask:           'returnBack',
    EMP_ReturnCoordination:         'returnBack',
    EMP_ReturnConnection:           'returnBack',
    EMP_ReturnAskAdvise:            'returnBack',
    EMP_Revoke:                     'revoke',
    EMP_ApproveDossier:             'approve',
    EMP_DecisionStopCounting:       'approve',
    ApproveRequestPause:            'approve',
    EMP_RefuseDossier:              'refuse',
    EMP_RefuseAskAdvise:            'refuse',
    EMP_RefuseConnect:              'refuse',
    EMP_RefuseStopCounting:         'refuse',
    RefuseRequestPause:             'refuse',
    EMP_PrintReceiptsAfterCreated:  'print',
    EMP_ReturnResult:               'returnResult',
    EMP_StopDossierToCouting:       'pause',
    EMP_ForwardRequestStopCounting: 'pause',
    CreateRequestPause:             'pause',
    ForwardRequestPause:            'pause',
    EMP_Cancel:                     'cancel',
    EMP_CancelStopCounting:         'cancel',
    CanclePause:                    'cancel',
    EMP_AssignTask:                 'assign',
    EMP_ReceiveConnect:             'connect',
    EMP_Coordination:               'connect',
    EMP_SendConnection:             'connect',
    EMP_AskAdvise:                  'connect',
    EMP_ReceiveAskAdvise:           'connect',
    EMP_ReceiveAdditional:          'additional',
    CustomerAdditional:             'additional',
    EMP_ContinueHandle:             'continue',
    Dossier:                        'general',
};

function getActionStyle(
    trace: string | null | undefined,
    title: string | null | undefined
): ActionStyle {
    const key = trace ?? title ?? '';
    const category = ACTION_TRACE_CATEGORY[key] ?? 'unknown';
    return ACTION_STYLES[category];
}

const DossierNotificationList = ({
    isFetching = false,
    isFetchMore = false,
    hvMoreNotice = false,
    onFetchMore,
    onMarkAllRead,
    isMarkingAll = false,
}: NotificationListProps) => {
    const transCmp = useTranslations('NotificationList');
    const transDef = useTranslations('System');

    const { totalNotification, arrNotice, totalUnRead } =
        useDossierNotificationStore((x) => x);
    const { isCustomer, setOpen } = useHeaderNotificationDropdownStore(
        (x) => x
    );

    const [isFetchEmp, setIsFetchEmp] = useState(false);
    const [objEmployeeInfo, setObjEmployeeInfo] = useState<
        Record<string, EmployeeSearchAdvanceResponse | undefined>
    >({});

    const _divScroll = useRef<HTMLDivElement>(null);
    const { getCachedData } = useCachedLocalEmployeeInfo();

    const onFetchCachedEmpInfo = useCallback(
        async (empIds: string[]) => {
            setIsFetchEmp(true);
            const result = await getCachedData(empIds);
            setObjEmployeeInfo(result);
            setIsFetchEmp(false);
        },
        [getCachedData]
    );

    const handleScroll = useCallback(() => {
        if (isFetchMore) return;
        const element = _divScroll.current;
        if (element) {
            const { scrollTop, scrollHeight, clientHeight } = element;
            if (
                scrollHeight - scrollTop <= clientHeight + 100 &&
                hvMoreNotice
            ) {
                onFetchMore();
            }
        }
    }, [hvMoreNotice, isFetchMore, onFetchMore]);

    useEffect(() => {
        if (arrNotice.length) {
            const { empIds } = collectionEmployeeId(arrNotice);
            if (empIds.length) onFetchCachedEmpInfo(empIds);
        }
    }, [arrNotice, onFetchCachedEmpInfo]);

    useEffect(() => {
        const el = _divScroll.current;
        if (el) {
            el.addEventListener('scroll', handleScroll);
            return () => el.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    /**
     * Số lượng chưa đọc dựa theo đúng cột:
     * - Customer: `Customer_IsSeen`
     * - Nhân viên: `IsSeen`
     */
    const unreadCount = arrNotice.filter(
        (n) => !getIsSeenByUser(n, isCustomer)
    ).length;

    return (
        <div className='flex flex-col'>
            {/* Tiêu đề panel + nút đánh dấu tất cả đã đọc (chỉ nhân viên) */}
            <div className='flex items-center justify-between border-b px-4 py-3'>
                <div className='flex items-center gap-2'>
                    <span className='text-sm font-semibold text-gray-900'>
                        {transCmp('NotificationTitle')}
                    </span>
                    {totalUnRead > 0 && (
                        <Badge className='h-5 min-w-5 rounded-full bg-blue-500 px-1.5 text-[10px] text-white'>
                            {totalUnRead > 99 ? '99+' : totalUnRead}
                        </Badge>
                    )}
                </div>

                {/* Nút "Đánh dấu tất cả đã đọc" — KHÔNG áp dụng cho người nộp */}
                {!isCustomer && unreadCount > 0 && onMarkAllRead && (
                    <Button
                        variant='ghost'
                        size='sm'
                        className='h-7 gap-1 px-2 text-xs text-blue-600 hover:bg-blue-50 hover:text-blue-700'
                        onClick={onMarkAllRead}
                        disabled={isMarkingAll}
                    >
                        <Check size={12} strokeWidth={2.5} />
                        {transCmp('MarkAllAsRead')}
                    </Button>
                )}
            </div>

            {/* Danh sách thông báo — có scroll */}
            <div
                ref={_divScroll}
                className='max-h-[calc(60vh-6rem)] overflow-y-auto'
            >
                {arrNotice.length > 0 ? (
                    <div className='divide-y divide-gray-100'>
                        {arrNotice.map((notice) => (
                            <NotificationItem
                                key={notice.ID}
                                notice={notice}
                                objEmpInfo={objEmployeeInfo}
                                isFetchEmpInfo={isFetchEmp}
                            />
                        ))}
                        {isFetchMore && (
                            <CustomLoading.Wrapper loading>
                                <div className='min-h-20' />
                            </CustomLoading.Wrapper>
                        )}
                    </div>
                ) : (
                    !isFetching && (
                        /* Trạng thái rỗng */
                        <div className='flex min-h-48 flex-col items-center justify-center gap-3 py-8'>
                            <div className='flex h-14 w-14 items-center justify-center rounded-full bg-gray-100'>
                                <BellOff size={24} className='text-gray-400' />
                            </div>
                            <p className='text-sm font-medium text-gray-500'>
                                {transCmp('YouHaveNoNotice')}
                            </p>
                        </div>
                    )
                )}
            </div>

            {/* Footer: số lượng + nút xem tất cả (chỉ nhân viên) */}
            {arrNotice.length > 0 && (
                <div className='flex items-center justify-between border-t bg-gray-50/70 px-4 py-2.5'>
                    <span className='text-xs text-muted-foreground'>
                        {transDef('System_Show')} {arrNotice.length}/
                        {totalNotification} {transCmp('Notice').toLowerCase()}
                    </span>

                    {!isCustomer && (
                        <Link
                            href={pageDirectories['Notification']}
                            onClick={() => setOpen(false)}
                            className='flex items-center gap-0.5 text-xs font-medium text-blue-600 hover:text-blue-700'
                        >
                            {transCmp('ViewAll')}
                            <ChevronRight size={12} />
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
};

const NotificationItem = memo(
    ({
        notice,
        objEmpInfo,
        isFetchEmpInfo,
    }: {
        notice: DossierNotificationResponse;
        isFetchEmpInfo?: boolean;
        objEmpInfo: Record<string, EmployeeSearchAdvanceResponse | undefined>;
    }) => {
        const transDossierPage = useTranslations('DossierManagement');
        const transCmp = useTranslations('NotificationList');
        const transDef = useTranslations('System');

        const apiUtils = api.useUtils();
        const router = useRouter();
        const pathname = usePathname();

        const empData = useEmployeeInfoStore((x) => x.current);
        const setStoreState = useDossierNotificationStore(
            (x) => x.setStoreState
        );

        const { isCustomer, setOpen: setOpenDropdown } =
            useHeaderNotificationDropdownStore((x) => x);

        const [isLoading, setIsLoading] = useState(false);
        /**Trạng thái mở popover xác nhận đánh dấu đã xem (chỉ cho người nộp) */
        const [showSeenConfirm, setShowSeenConfirm] = useState(false);

        /**
         * Trạng thái đã xem theo đúng đối tượng:
         * - Người nộp: dùng `Customer_IsSeen`
         * - Nhân viên: dùng `IsSeen`
         */
        const isSeenByCurrentUser = getIsSeenByUser(notice, isCustomer);

        /**
         * Kiểm tra người dùng đang ở trang chi tiết hồ sơ tương ứng.
         * Nếu đúng, không cần hỏi xác nhận khi đánh dấu đã xem.
         */
        const isOnThisDossierPage =
            isCustomer &&
            !!notice.DossierCode &&
            pathname.includes(notice.DossierCode);

        /**
         * Đọc thông báo — dùng chung cho nhân viên và người nộp.
         * - Nhân viên: truyền `EmployeeID`, cập nhật cột `IsSeen` trong store
         * - Người nộp: không truyền `EmployeeID`, cập nhật cột `Customer_IsSeen`
         * @param showToast Hiển thị toast thành công (chỉ khi người dùng chủ động bấm)
         */
        const onReadNotice = useCallback(
            async (
                noticeItem: DossierNotificationResponse,
                showToast = false
            ) => {
                if (!noticeItem.Code) return;
                setIsLoading(true);

                const sendData: DossierNotificationReadsRequest = {
                    Codes: [noticeItem.Code],
                    Client: 'MotCuaWebApp',
                    // Chỉ truyền EmployeeID cho nhân viên
                    ...(empData?.ID && !isCustomer
                        ? { EmployeeID: empData.ID }
                        : {}),
                };
                const result = await readDossierNotifications(sendData);

                if (!result?.data?.isSuccessStatus) {
                    console.error(result?.data);
                } else {
                    setStoreState((st) => {
                        let totalUnread = st?.totalUnRead ?? 0;
                        const arr = [...(st?.arrNotice ?? [])];
                        const idx = arr.findIndex(
                            (x) => x.ID === noticeItem.ID
                        );
                        if (idx > -1) {
                            // Cập nhật đúng cột theo đối tượng
                            if (isCustomer) {
                                arr[idx] = {
                                    ...arr[idx],
                                    Customer_IsSeen: true,
                                };
                            } else {
                                arr[idx] = { ...arr[idx], IsSeen: true };
                            }
                            if (totalUnread > 0) totalUnread--;
                        }
                        return { arrNotice: arr, totalUnRead: totalUnread };
                    });

                    if (showToast) {
                        CustomToast.sonner.success(
                            transCmp('MarkSeenSuccess'),
                            { duration: 3000, position: 'top-right' }
                        );
                    }
                }

                setIsLoading(false);
            },
            [empData?.ID, isCustomer, setStoreState, transCmp]
        );

        /**
         * Click vào item: điều hướng + tự động đánh dấu đã đọc (không hiện toast).
         * Dùng `isSeenByCurrentUser` để kiểm tra theo đúng đối tượng.
         */
        const onClickNotice = useCallback(
            (noticeItem: DossierNotificationResponse) => {
                if (noticeItem.DossierCode) {
                    const url = isCustomer
                        ? `${pageDirectories['Customer_SubmitDossier']}/${noticeItem.DossierCode}`
                        : `${pageDirectories['DossierProcessing']}/${noticeItem.DossierCode}`;
                    router.push(url);
                }

                const isSeen = getIsSeenByUser(noticeItem, isCustomer);
                if (!isLoading && !isSeen) {
                    onReadNotice(noticeItem, false);
                }

                setOpenDropdown(false);
            },
            [isCustomer, isLoading, onReadNotice, router, setOpenDropdown]
        );

        /**
         * Người nộp bấm nút Eye "đánh dấu đã xem":
         * - Đang ở trang hồ sơ trùng → đánh dấu trực tiếp
         * - Chưa ở trang hồ sơ → mở popover xác nhận
         */
        const onCustomerMarkSeenClick = useCallback(
            (e: React.MouseEvent) => {
                e.stopPropagation();
                if (isOnThisDossierPage) {
                    onReadNotice(notice, true);
                } else {
                    setShowSeenConfirm((v) => !v);
                }
            },
            [isOnThisDossierPage, notice, onReadNotice]
        );

        /**Reload nếu đã bỏ qua đủ số lượng thông báo */
        const onReloadNoticeIfNeed = useCallback(
            (before: number, after: number) => {
                if (before - 5 === after) {
                    apiUtils.appNotification.GetPage.invalidate();
                }
            },
            [apiUtils.appNotification.GetPage]
        );

        /**Ẩn thông báo (không xóa khỏi DB) */
        const onRemoveNotice = useCallback(
            async (id: string) => {
                setIsLoading(true);

                const result = await hideDossierNotifications([id]);

                if (!result?.data?.isSuccessStatus) {
                    console.error(result?.data);
                } else {
                    setStoreState((st) => {
                        const newArr = [...(st?.arrNotice ?? [])];
                        let totalNotice = st?.totalNotification ?? 0;
                        const idx = newArr.findIndex((x) => x.ID === id);
                        if (idx > -1) {
                            newArr.splice(idx, 1);
                            totalNotice--;
                        }
                        onReloadNoticeIfNeed(
                            st?.arrNotice?.length ?? 0,
                            newArr.length
                        );
                        return {
                            arrNotice: newArr,
                            totalNotification: totalNotice,
                            totalUnRead: (st?.totalUnRead ?? 1) - 1,
                        };
                    });
                }
                setIsLoading(false);
            },
            [onReloadNoticeIfNeed, setStoreState]
        );

        const actionStyle = getActionStyle(notice.ActionTriggerTrace, notice.Title);
        const ActionIcon = actionStyle.icon;

        /**
         * Nhãn hành động của thông báo (tra từ ProcessActionDirectories).
         * Ưu tiên: ProcessActionDirectories[Title] → ProcessActionDirectories[ActionTriggerTrace] → Title gốc.
         */
        const renderLabelTTHS = useMemo(() => {
            if (notice.ActionTriggerTrace || notice.Title) {
                const lang_label =
                    ProcessActionDirectories?.[notice.Title!] ||
                    ProcessActionDirectories?.[notice.ActionTriggerTrace!];
                if (lang_label) return transDossierPage(lang_label);
            }
            return notice.Title ?? undefined;
        }, [notice.ActionTriggerTrace, notice.Title, transDossierPage]);

        return (
            <div
                className={cn(
                    'group relative cursor-pointer border-l-[3px] px-4 py-3 transition-all duration-150',
                    'hover:bg-gray-50 active:bg-gray-100',
                    {
                        /* Chưa xem theo đối tượng: viền xanh + nền xanh nhạt */
                        'border-l-blue-500 bg-blue-50/40': !isSeenByCurrentUser,
                        /* Đã xem: viền trong suốt + nền trắng */
                        'border-l-transparent bg-white': isSeenByCurrentUser,
                        'cursor-progress': isLoading,
                    }
                )}
                onClick={() => onClickNotice(notice)}
                role='link'
                title={`Xem chi tiết xử lý hồ sơ ${notice.ReceivingNumber ?? ''}`}
            >
                <div className='flex gap-3'>
                    {/* Icon avatar — màu theo loại hành động (chưa đọc) hoặc xám (đã đọc) */}
                    <TooltipProvider delayDuration={400}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div
                                    className={cn(
                                        'mt-0.5 flex h-9 w-9 flex-shrink-0 cursor-default items-center justify-center rounded-full',
                                        isSeenByCurrentUser
                                            ? 'bg-gray-100'
                                            : actionStyle.bgClass
                                    )}
                                >
                                    <ActionIcon
                                        size={16}
                                        className={
                                            isSeenByCurrentUser
                                                ? 'text-gray-400'
                                                : actionStyle.iconClass
                                        }
                                    />
                                </div>
                            </TooltipTrigger>
                            <TooltipContent side='left' className='text-xs'>
                                {actionStyle.tooltip}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>

                    {/* Nội dung thông báo */}
                    <div className='min-w-0 flex-1'>
                        {/* Nhãn hành động / trạng thái hồ sơ */}
                        <p
                            className={cn('line-clamp-1 pr-6 text-sm', {
                                'font-semibold text-gray-900':
                                    !isSeenByCurrentUser,
                                'font-normal text-gray-700': isSeenByCurrentUser,
                            })}
                        >
                            {renderLabelTTHS || '—'}
                        </p>

                        {/* Meta — hiển thị khác nhau theo đối tượng */}
                        <div className='mt-0.5 space-y-0.5 text-xs text-gray-500'>
                            {notice.ReceivingNumber && (
                                <p className='truncate'>
                                    {transCmp('ReceivedNumber')}:{' '}
                                    <span className='font-medium text-gray-700'>
                                        {notice.ReceivingNumber}
                                    </span>
                                </p>
                            )}

                            {/*
                             * Nhân viên: hiển thị tên người nộp (Customer_FullName)
                             * Người nộp: ẩn — tên của họ không cần thiết, thay bằng chuyên viên xử lý
                             */}
                            {!isCustomer && notice.Customer_FullName && (
                                <p className='truncate'>
                                    {transCmp('IndividualOrOrganization')}:{' '}
                                    <span className='font-medium text-gray-700'>
                                        {notice.Customer_FullName}
                                    </span>
                                </p>
                            )}

                            {/*
                             * Cả hai đối tượng: hiển thị người gửi thông báo
                             * Với nhân viên: là nhân viên gửi
                             * Với người nộp: là chuyên viên xử lý hồ sơ của họ
                             */}
                            {notice.Send_Employee_FullName && (
                                <p className='truncate'>
                                    {transCmp('From')}:{' '}
                                    <span className='font-medium text-gray-700'>
                                        {notice.Send_Employee_FullName}
                                    </span>
                                </p>
                            )}
                        </div>

                        {/* Thời gian + nút bỏ qua */}
                        <div className='mt-1.5 flex items-center justify-between gap-2'>
                            <div className='flex items-center gap-1 text-xs text-gray-400'>
                                <Clock size={11} />
                                <span>
                                    {formatDateTime(
                                        notice.SendDate,
                                        'dd/MM/yyyy HH:mm'
                                    )}
                                </span>
                            </div>

                            <button
                                type='button'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isLoading) onRemoveNotice(notice.ID!);
                                }}
                                className='invisible flex cursor-pointer items-center gap-0.5 text-xs text-gray-400 transition-colors hover:text-red-500 group-hover:visible'
                            >
                                <X size={12} />
                                <span>{transCmp('Skip')}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/*
                 * Góc trên phải:
                 * - Người nộp chưa xem (Customer_IsSeen = false): nút Eye + popover xác nhận
                 * - Nhân viên chưa xem (IsSeen = false): chấm xanh tĩnh
                 */}
                {!isSeenByCurrentUser && (
                    isCustomer ? (
                        <div
                            className='absolute right-2 top-2'
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Popover
                                open={showSeenConfirm}
                                onOpenChange={setShowSeenConfirm}
                            >
                                <PopoverTrigger asChild>
                                    <button
                                        type='button'
                                        disabled={isLoading}
                                        onClick={onCustomerMarkSeenClick}
                                        className={cn(
                                            'flex h-6 w-6 items-center justify-center rounded-full',
                                            'bg-blue-100 text-blue-500 transition-colors',
                                            'hover:bg-blue-200 hover:text-blue-600',
                                            'disabled:cursor-not-allowed disabled:opacity-50'
                                        )}
                                        title='Đánh dấu đã xem'
                                    >
                                        <Eye size={13} strokeWidth={2} />
                                    </button>
                                </PopoverTrigger>

                                {/* Popover xác nhận — render trong Radix portal, không bị overflow cắt */}
                                <PopoverContent
                                    className='w-64 p-3'
                                    side='left'
                                    align='start'
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <p className='text-sm font-semibold text-gray-900'>
                                        Đánh dấu đã xem?
                                    </p>
                                    <p className='mt-1 text-xs text-gray-500'>
                                        {transCmp('ConfirmMarkSeen')}
                                    </p>
                                    <div className='mt-3 flex justify-end gap-2'>
                                        <Button
                                            variant='ghost'
                                            size='sm'
                                            className='h-7 px-2 text-xs'
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowSeenConfirm(false);
                                            }}
                                        >
                                            {transDef('System_Cancel')}
                                        </Button>
                                        <Button
                                            size='sm'
                                            className='h-7 px-2 text-xs'
                                            disabled={isLoading}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowSeenConfirm(false);
                                                onReadNotice(notice, true);
                                            }}
                                        >
                                            {transDef('System_Confirm')}
                                        </Button>
                                    </div>
                                </PopoverContent>
                            </Popover>
                        </div>
                    ) : (
                        /* Nhân viên: chấm xanh tĩnh — đánh dấu tự động khi click item */
                        <span className='absolute right-3 top-3 h-2 w-2 rounded-full bg-blue-500' />
                    )
                )}
            </div>
        );
    }
);
NotificationItem.displayName = 'NotificationItem';

export default memo(DossierNotificationList);
